import React, { useState, useMemo } from 'react';
import {
  Box, 
  Typography, 
  Button, 
  Grid, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Popover
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TemplateIcon from '@mui/icons-material/Description';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { apiTemplateCategories, apiTemplates, ApiTemplate, HttpMethod } from './GraphApiTemplates';

interface GraphApiTemplatesComponentProps {
  onApplyTemplate: (method: HttpMethod, endpoint: string, body: string, headers: string) => void;
  isReadOnly?: boolean; // Add read-only prop
}

export const GraphApiTemplatesComponent: React.FC<GraphApiTemplatesComponentProps> = ({ 
  onApplyTemplate,
  isReadOnly = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('applications');
  const [templateDetailOpen, setTemplateDetailOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ApiTemplate | null>(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [favoriteTemplates, setFavoriteTemplates] = useState<string[]>(() => {
    // Load favorites from localStorage if available
    const savedFavorites = localStorage.getItem('graphApiTemplateFavorites');
    return savedFavorites ? JSON.parse(savedFavorites) : [];
  });
  
  // New state for the info popover
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const infoPopoverOpen = Boolean(infoAnchorEl);
  
  // Handler for info icon click
  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setInfoAnchorEl(event.currentTarget);
  };

  // Handler for closing the info popover
  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };
  
  // Handler for toggling a template as favorite
  const toggleFavorite = (templateId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent opening template details
    
    let updatedFavorites: string[];
    if (favoriteTemplates.includes(templateId)) {
      updatedFavorites = favoriteTemplates.filter(id => id !== templateId);
    } else {
      updatedFavorites = [...favoriteTemplates, templateId];
    }
    
    setFavoriteTemplates(updatedFavorites);
    localStorage.setItem('graphApiTemplateFavorites', JSON.stringify(updatedFavorites));
  };
  
  // Filter templates based on search query, selected category, favorites, and read-only mode
  const filteredTemplates = useMemo(() => {
    let templates = apiTemplates;
    
    // Filter to only GET requests if in read-only mode
    if (isReadOnly) {
      templates = templates.filter(template => template.method === 'GET');
    }
    
    // Filter by category
    if (selectedCategory === 'favorites') {
      templates = templates.filter(template => favoriteTemplates.includes(template.id));
    } else if (selectedCategory !== 'all') {
      templates = templates.filter(template => template.category === selectedCategory);
    }
    
    // Then filter by search query if any
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      templates = templates.filter(template => 
        template.name.toLowerCase().includes(query) || 
        template.endpoint.toLowerCase().includes(query) || 
        template.description.toLowerCase().includes(query)
      );
    }
    
    return templates;
  }, [selectedCategory, searchQuery, favoriteTemplates, isReadOnly]);

  // Handler for when a template is selected
  const handleTemplateSelect = (template: ApiTemplate) => {
    setSelectedTemplate(template);
    setTemplateDetailOpen(true);
  };

  // Handler for applying the selected template
  const confirmApplyTemplate = () => {
    if (!selectedTemplate) return;
    
    onApplyTemplate(
      selectedTemplate.method,
      selectedTemplate.endpoint,
      selectedTemplate.body,
      selectedTemplate.headers
    );
    
    setTemplateDetailOpen(false);
  };

  return (
    <Box sx={{ mb: 3, p: 2, backgroundColor: '#f4f8fd', borderRadius: 2, border: '1px solid #e3f2fd' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: 'primary.main',
            fontWeight: 600 
          }}
        >
          {isReadOnly ? <VisibilityIcon sx={{ fontSize: 18, mr: 1 }} /> : <MenuBookIcon sx={{ fontSize: 18, mr: 1 }} />}
          {isReadOnly ? 'Read-Only API Templates' : 'API Templates'}
          <Tooltip title={isReadOnly ? "About Read-Only API Templates" : "About API Templates"}>
            <IconButton 
              size="small" 
              onClick={handleInfoClick}
              sx={{ ml: 0.5, color: 'info.main' }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          
          {/* Info Popover */}
          <Popover
            open={infoPopoverOpen}
            anchorEl={infoAnchorEl}
            onClose={handleInfoClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: { 
                width: 320, 
                p: 2,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {isReadOnly ? 'About Read-Only API Templates' : 'About API Templates'}
            </Typography>
            <Typography variant="body2" paragraph>
              {isReadOnly 
                ? 'Explore common Graph API read operations. All templates use GET requests to retrieve data without making any changes.'
                : 'Explore common Graph API operations by category. Select a template to quickly populate your request with pre-defined endpoints, methods and parameters.'
              }
            </Typography>
            <Typography variant="body2">
              • Click <strong>Show Templates</strong> to browse available {isReadOnly ? 'read-only ' : ''}templates
            </Typography>
            <Typography variant="body2">
              • Use ⭐ to mark your favorite templates for quick access
            </Typography>
            <Typography variant="body2">
              • Select a template to view details before applying it
            </Typography>
            {isReadOnly && (
              <Alert severity="info" sx={{ mt: 1, borderRadius: 1 }}>
                <Typography variant="caption">
                  Only GET requests are available in read-only mode
                </Typography>
              </Alert>
            )}
          </Popover>
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isReadOnly && (
            <Chip 
              label="Read-Only" 
              size="small" 
              sx={{ 
                backgroundColor: '#eeeeee',
                color: '#424242',
                fontWeight: 500
              }}
            />
          )}
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => setQuickViewOpen(!quickViewOpen)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {quickViewOpen ? "Hide Templates" : "Show Templates"}
          </Button>
        </Box>
      </Box>

      {quickViewOpen && (
        <>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel id="api-category-label">Category</InputLabel>
                  <Select
                    labelId="api-category-label"
                    id="api-category-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value as string)}
                    label="Category"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        All Categories {isReadOnly && '(GET only)'}
                      </Box>
                    </MenuItem>
                    <MenuItem value="favorites">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StarIcon sx={{ fontSize: 18, mr: 1, color: 'gold' }} />
                        Favorites {isReadOnly && '(GET only)'}
                      </Box>
                    </MenuItem>
                    {apiTemplateCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <category.icon sx={{ fontSize: 18, mr: 1 }} />
                          {category.name} {isReadOnly && '(GET only)'}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={isReadOnly ? "Search read-only templates..." : "Search templates..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {isReadOnly && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Read-Only Mode:</strong> Only GET request templates are shown. These allow you to retrieve data without making any changes to your Azure AD configuration.
              </Typography>
            </Alert>
          )}

          <Grid container spacing={1}>
            {filteredTemplates.length > 0 ? (
              filteredTemplates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template.id}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    onClick={() => handleTemplateSelect(template)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                        borderColor: 'primary.main',
                      },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flex: 1 }}>
                      <TemplateIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', flexShrink: 0 }} />
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          {template.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <Chip 
                            label={template.method} 
                            size="small" 
                            color="info" // Always info (blue) since only GET requests in read-only mode
                            sx={{ height: 18, fontSize: '0.675rem', mr: 0.5 }}
                          />
                          {template.endpoint.length > 20 
                            ? `${template.endpoint.substring(0, 20)}...` 
                            : template.endpoint
                          }
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={(e) => toggleFavorite(template.id, e)}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      {favoriteTemplates.includes(template.id) ? (
                        <StarIcon fontSize="small" sx={{ color: 'gold' }} />
                      ) : (
                        <StarBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {isReadOnly 
                    ? "No matching read-only templates found. Try changing your search or category selection."
                    : "No matching templates found. Try changing your search or category selection."
                  }
                </Alert>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* Template Detail Dialog */}
      <Dialog
        open={templateDetailOpen}
        onClose={() => setTemplateDetailOpen(false)}
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        {selectedTemplate && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'center',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: '#f9f9f9'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <TemplateIcon sx={{ mr: 1, color: 'primary.main' }} />
                {selectedTemplate.name}
                {isReadOnly && (
                  <Chip 
                    label="Read-Only" 
                    size="small" 
                    sx={{ 
                      ml: 1,
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2'
                    }}
                  />
                )}
              </Box>
              <IconButton
                onClick={(e) => toggleFavorite(selectedTemplate.id, e)}
                size="small"
              >
                {favoriteTemplates.includes(selectedTemplate.id) ? (
                  <StarIcon fontSize="small" sx={{ color: 'gold' }} />
                ) : (
                  <StarBorderIcon fontSize="small" />
                )}
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Typography variant="body2" paragraph>
                {selectedTemplate.description}
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                  Request Details:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Method:</Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Chip 
                      label={selectedTemplate.method} 
                      size="small" 
                      color="info" // Always info since only GET in read-only mode
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Endpoint:</Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Typography variant="body2" sx={{ 
                      fontFamily: 'monospace', 
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      padding: '4px 8px',
                      borderRadius: 1
                    }}>
                      {selectedTemplate.endpoint}
                    </Typography>
                  </Grid>
                  
                  {/* Remove body section since GET requests don't have body */}
                  
                  {selectedTemplate.headers && selectedTemplate.headers !== '{}' && (
                    <>
                      <Grid item xs={12} sm={3}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Headers:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <Box
                          sx={{
                            maxHeight: '80px',
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            padding: '8px',
                            borderRadius: 1,
                            '& pre': {
                              margin: 0
                            }
                          }}
                        >
                          <pre>{selectedTemplate.headers}</pre>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <Alert severity="info" sx={{ borderRadius: 2, mt: 1 }}>
                  <Typography variant="caption">
                    Replace any placeholder values like {"{app-id}"} or {"{user-id}"} with actual IDs after applying the template.
                    {isReadOnly && " This template will only retrieve data without making any changes."}
                  </Typography>
                </Alert>
              </Paper>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Button 
                onClick={() => setTemplateDetailOpen(false)}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmApplyTemplate}
                variant="contained"
                startIcon={isReadOnly ? <VisibilityIcon /> : <PlayArrowIcon />}
                sx={{ borderRadius: 2 }}
              >
                {isReadOnly ? 'View Data' : 'Use Template'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default GraphApiTemplatesComponent;